import { useState, useEffect } from "react";
import { useLoaderData, useNavigate } from "@remix-run/react";
import SeekIcon from "~/components/icons/seek-icon.svg";
import { ErrorFlash } from "~/components/flash";
import { SeekAuthTab } from "../utils/seek_auth_tab.client";
import type { SeekLoaderData } from "../utils/seek_loader.server";
import { Button, type ButtonShape } from "~/components/button";
import { useTranslation } from "react-i18next";
import "./apply_with_seek_button.scss";

export const ApplyWithSeekButton = ({ button_shape }: { button_shape?: ButtonShape | null }) => {
  const { t } = useTranslation("job_post");
  const navigate = useNavigate();
  const [isLoadingSeek, setIsLoadingSeek] = useState(false);

  const loaderData = useLoaderData<SeekLoaderData>();
  const seekData = loaderData?.seek;

  const applyWithSeekEnabled = seekData?.applyWithSeekEnabled ?? false; //env var
  const isConfigured = seekData?.isConfigured ?? false; //job board setting
  const isPreview = seekData?.isPreview ?? false;
  const applyWithSeek = seekData?.applyWithSeek; //job post setting
  const seekAuthUrl = seekData?.seekAuthUrl;
  const seekProfile = seekData?.seekProfile;
  const errorMessage = seekData?.error;
  const hasInvalidSeekParams = seekData?.hasInvalidSeekParams ?? false;

  useEffect(() => {
    if (hasInvalidSeekParams) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("seek-token");
      currentUrl.searchParams.delete("seek-prefill-id");
      // Use navigate with replace to update URL without full page reload
      // This preserves form state without a full page reload
      navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
    }
  }, [hasInvalidSeekParams, navigate]);

  if (!seekData) {
    return null;
  }

  const handleApplyWithSeekClick = async () => {
    if (isPreview) {
      return;
    }

    // If profile already exists, refetch it to get any new info
    if (seekProfile) {
      setIsLoadingSeek(true);
      try {
        const currentUrl = new URL(window.location.href);
        // Add a timestamp query param to force Remix to refetch the loader
        // The loader will ignore this param and just refetch the Seek profile
        currentUrl.searchParams.set("_seek-refresh", Date.now().toString());
        // Navigate to trigger loader refetch
        // This will preserve form state and refetch the Seek profile
        navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
      } finally {
        setIsLoadingSeek(false);
      }
      return;
    }

    if (!seekAuthUrl) {
      console.error("Seek auth URL is not available");
      return;
    }

    setIsLoadingSeek(true);
    try {
      const authTab = new SeekAuthTab();
      const result = await authTab.authenticate(seekAuthUrl);

      if (result && (result.seekToken || result.seekPrefillId)) {
        const currentUrl = new URL(window.location.href);
        if (result.seekToken) {
          currentUrl.searchParams.set("seek-token", result.seekToken);
        }
        if (result.seekPrefillId) {
          currentUrl.searchParams.set("seek-prefill-id", result.seekPrefillId);
        }

        // Use navigate with replace to update URL without full page reload
        // This preserves form state without a full page reload
        navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
      }
    } finally {
      setIsLoadingSeek(false);
    }
  };

  // Render button if:
  // - Seek is configured (based on the 3 conditions below)
  //   - applyWithSeekEnabled: Seek is enabled globally (via env var) AND
  //   - isConfigured: the job board is configured for apply with seek (has seekId) AND
  //   - applyWithSeek: the job post has apply_with_seek enabled
  if (applyWithSeekEnabled && isConfigured && applyWithSeek) {
    return (
      <>
        {errorMessage && <ErrorFlash message={errorMessage} />}
        <Button
          testId="apply-with-seek-button"
          onClick={() => handleApplyWithSeekClick()}
          disabled={isPreview || !!errorMessage || isLoadingSeek}
          aria-label="Apply with Seek"
          type="button"
          secondary={true}
          shape={button_shape}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <img src={SeekIcon} alt="Apply with Seek" />
            {t("application.quick_apply.apply_with_seek")}
          </span>
        </Button>
      </>
    );
  }

  return null;
};

export default ApplyWithSeekButton;
